import { Component, signal, effect, inject, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  ActualiteService,
  Message,
  Commentaire,
  PagedResponse
} from '../../core/services/actualite.service';
import { SessionService } from '../../core/session.service';
import { ToastService } from '../../core/services/toast.service';
import { ConfirmModalComponent } from '../../component/confirm-modal/confirm-modal.component';
import { MessageTemplateService, MessageTemplate } from '../../core/services/message-template.service';
import { PageSiteSwitcherComponent } from '../../component/page-site-switcher/page-site-switcher.component';



@Component({
  selector: 'app-actualite',
  standalone: true,
  imports: [CommonModule, FormsModule, PickerModule, ConfirmModalComponent, PageSiteSwitcherComponent, TranslatePipe],
  templateUrl: './actualite.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./actualite.component.scss']
})
export class ActualiteComponent {
  private api = inject(ActualiteService);
  private session = inject(SessionService);
  private toastService = inject(ToastService);
  private templateService = inject(MessageTemplateService);
  private translate = inject(TranslateService);

  loading = signal<boolean>(false);
  error = signal<string | null>(null);
  data = signal<PagedResponse<Message> | null>(null);
  allMessages = signal<Message[]>([]);
  filteredMessages = signal<Message[]>([]);
  currentPage = signal<number>(0);
  pageSize = signal<number>(10);

  // Templates
  newsTemplates = signal<MessageTemplate[]>([]);

  // Formulaire nouveau message
  selectedTemplate = '';
  nouveauTitre = '';
  nouveauMessage = '';



  // Filtres
  filtreType = 'Tous';

  // Commentaires
  showComments = signal<Record<string, boolean>>({});
  nouveauCommentaire = signal<Record<string, string>>({});

  // Expansion des messages
  expandedMessages = signal<Record<string, boolean>>({});

  // Modale de confirmation
  showConfirmModal = signal(false);
  confirmModalTitle = signal('');
  confirmModalMessage = signal('');
  pendingAction: (() => void) | null = null;

  // Édition
  editingMessageId = signal<string | null>(null);
  editingCommentaireId = signal<string | null>(null);
  editTitre = '';
  editContenu = '';


  users = signal<{id: string, name: string, email: string}[]>([]);
  userMap = new Map<string, string>();
  private loadedEstablishmentId: string | null = null;
  showMentionDropdown = false;
  showCommentMentionDropdown = signal<Record<string, boolean>>({});
  filteredUsers: {id: string, name: string, email: string}[] = [];
  showEmojiPicker = false;
  showCommentEmojiPicker = signal<Record<string, boolean>>({});
  showComposer = signal<boolean>(window.innerWidth > 640);



  constructor() {
    effect(() => {
      const estId = this.session.selectedId();
      if (estId) {
        this.loadMessages(estId);
        this.loadUsers(estId);
        this.markMentionsAsRead(estId);
        this.loadNewsTemplates(estId);
      }
    });
  }

  private loadNewsTemplates(establishmentId: string) {
    const establishment = this.session.establishments().find(e => e.id === establishmentId);
    if (!establishment?.org_id) return;

    this.templateService.getTemplatesByType('NEWS_FEED', establishment.org_id)
      .subscribe({
        next: (templates) => this.newsTemplates.set(templates),
        error: (err) => console.error('Error loading news templates:', this.sanitizeForLog(err))
      });
  }

  private markMentionsAsRead(establishmentId: string) {
    this.api.markMentionsAsRead(establishmentId).subscribe({
      next: () => {
        // Les mentions sont marquées comme lues, le shell se mettra à jour automatiquement
      },
      error: (err) => {
        // Ignorer silencieusement les erreurs 404/403 en attendant la mise à jour du backend
        if (err.status !== 403 && err.status !== 404) {
          console.error('Erreur marquage mentions:', this.sanitizeForLog(err));
        }
      }
    });
  }



  private loadUsers(establishmentId: string) {
    if (this.loadedEstablishmentId === establishmentId) return;
    
    this.loadedEstablishmentId = establishmentId;
    this.api.getUsers(establishmentId).subscribe({
      next: (users) => {
        this.users.set(users);
        // Créer le map pour la recherche rapide avec mentionFormat
        this.userMap.clear();
        users.forEach(user => {
          if (!user.email || typeof user.email !== 'string' || !user.email.includes('@')) {
            return; // Skip invalid emails
          }
          const mentionFormat = (user as any).mentionFormat || user.email.split('@')[0];
          this.userMap.set(mentionFormat.toLowerCase(), user.id);
        });
      },
      error: (err) => {
        this.loadedEstablishmentId = null;
        if (err.status !== 404) {
          console.error('Erreur chargement utilisateurs:', this.sanitizeForLog(err));
          this.toastService.show(this.translate.instant('actualite.journal.toast.errorTitle'), 'error', 3000, this.translate.instant('actualite.journal.toast.usersLoadError'));
        }
      }
    });
  }

  private loadMessages(establishmentId: string) {
    this.loading.set(true);
    this.error.set(null);

    this.api.getMessages(
      establishmentId,
      this.currentPage(),
      this.pageSize()
    ).subscribe({
      next: (response) => {
        this.allMessages.set(response.content);
        this.applyFilters();
        this.data.set({
          ...response,
          content: this.filteredMessages()
        });

        // Déplier les commentaires du premier message s'il en a
        if (response.content.length > 0 && response.content[0].commentaires && response.content[0].commentaires.length > 0) {
          this.showComments.update(state => ({
            ...state,
            [response.content[0].id]: true
          }));
        }

        this.loading.set(false);
      },
      error: (err) => {
        if (err.status === 404) {
          this.error.set(this.translate.instant('actualite.journal.states.apiNotImplemented'));
        } else {
          console.error('Erreur chargement messages - Status:', this.sanitizeForLog(err?.status || 'unknown'));
          this.error.set(this.translate.instant('actualite.journal.states.loadError'));
        }
        this.loading.set(false);
      }
    });
  }

  private applyFilters() {
    let filtered = [...this.allMessages()];

    // Filtre par type
    if (this.filtreType !== 'Tous') {
      filtered = filtered.filter(m => m.type === this.filtreType);
    }

    // Cache des timestamps pour éviter les recalculs
    const activityCache = new Map<string, number>();

    const getLastActivity = (message: Message): number => {
      if (activityCache.has(message.id)) {
        return activityCache.get(message.id)!;
      }

      const messageDate = new Date(message.timestamp).getTime();
      let lastCommentDate = 0;

      if (message.commentaires && message.commentaires.length > 0) {
        for (const comment of message.commentaires) {
          const commentDate = new Date(comment.timestamp).getTime();
          if (commentDate > lastCommentDate) {
            lastCommentDate = commentDate;
          }
        }
      }

      const result = Math.max(messageDate, lastCommentDate);
      activityCache.set(message.id, result);
      return result;
    };

    // Tri par activité la plus récente
    filtered.sort((a, b) => getLastActivity(b) - getLastActivity(a));

    this.filteredMessages.set(filtered);
  }

  publierMessage() {
    const estId = this.session.selectedId();
    if (!estId || !this.nouveauTitre.trim() || !this.nouveauMessage.trim()) return;

    const errors = this.validateMessage(this.nouveauTitre, this.nouveauMessage);
    if (errors.length > 0) {
      this.toastService.show(this.translate.instant('actualite.journal.toast.errorTitle'), 'error', 4000, errors[0]);
      return;
    }

    const { mentionedUserIds, mentionAll } = this.extractMentions(this.nouveauMessage);

    this.api.createMessage(estId, {
      titre: this.nouveauTitre,
      contenu: this.nouveauMessage,
      mentioned_user_ids: mentionedUserIds,
      mention_all: mentionAll
    }).subscribe({
      next: () => {
        this.selectedTemplate = '';
        this.nouveauTitre = '';
        this.nouveauMessage = '';
        this.loadMessages(estId);
        this.toastService.show(this.translate.instant('actualite.journal.toast.successNoted'), 'success', 4000, this.translate.instant('actualite.journal.toast.messagePublished'));
      },
      error: (err) => {
        if (err.status === 404) {
          this.toastService.show(this.translate.instant('actualite.journal.toast.errorTitle'), 'error', 4000, this.translate.instant('actualite.journal.states.apiNotImplemented'));
        } else {
          console.error('Erreur publication message - Status:', this.sanitizeForLog(err?.status || 'unknown'));
          this.toastService.show(this.translate.instant('actualite.journal.toast.errorTitle'), 'error', 4000, this.translate.instant('actualite.journal.toast.publishError'));
        }
      }
    });
  }

  toggleComments(messageId: string) {
    this.showComments.update(state => ({
      ...state,
      [messageId]: !state[messageId]
    }));
  }

  ajouterCommentaire(messageId: string) {
    const estId = this.session.selectedId();
    const contenu = this.nouveauCommentaire()[messageId];
    if (!estId || !contenu?.trim()) return;

    const { mentionedUserIds, mentionAll } = this.extractMentions(contenu);

    this.api.addCommentaire(estId, messageId, {
      contenu,
      mentioned_user_ids: mentionedUserIds,
      mention_all: mentionAll
    }).subscribe({
      next: () => {
        this.nouveauCommentaire.update(state => ({ ...state, [messageId]: '' }));
        this.loadMessages(estId);
        this.toastService.show(this.translate.instant('actualite.journal.toast.successNoted'), 'success', 4000, this.translate.instant('actualite.journal.toast.commentAdded'));
      },
      error: (err) => {
        if (err.status === 404) {
          this.toastService.show(this.translate.instant('actualite.journal.toast.errorTitle'), 'error', 4000, this.translate.instant('actualite.journal.states.apiNotImplemented'));
        } else {
          console.error('Erreur ajout commentaire - Status:', err?.status || 'unknown');
          this.toastService.show(this.translate.instant('actualite.journal.toast.errorTitle'), 'error', 4000, this.translate.instant('actualite.journal.toast.commentAddError'));
        }
      }
    });
  }

  getTimeAgo(dateStr: string): string {
    if (!dateStr) return this.translate.instant('actualite.journal.time.now');

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return this.translate.instant('actualite.journal.time.now');

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return this.translate.instant('actualite.journal.time.now');
    if (diffMins < 60) return this.translate.instant('actualite.journal.time.minutesAgo', { minutes: diffMins });
    if (diffHours < 24) return this.translate.instant('actualite.journal.time.hoursAgo', { hours: diffHours });
    return this.translate.instant('actualite.journal.time.daysAgo', { days: diffDays });
  }

  isRecent(dateStr: string): boolean {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    return diffMs < 30 * 60000; // 30 minutes
  }

  isLongMessage(contenu: string): boolean {
    return contenu.length > 200;
  }

  toggleExpanded(messageId: string) {
    this.expandedMessages.update(state => ({
      ...state,
      [messageId]: !state[messageId]
    }));
  }

  getTruncatedContent(contenu: string): string {
    return contenu.substring(0, 200) + '...';
  }



  getInitials(name: string): string {
    const parts = name.split(/[\s.-]+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  onFilterChange() {
    // Appliquer les filtres sur les données déjà chargées
    this.applyFilters();
    const data = this.data();
    if (data) {
      this.data.set({
        ...data,
        content: this.filteredMessages()
      });
    }
  }

  goToPage(page: number) {
    const estId = this.session.selectedId();
    if (estId && page >= 0 && page < (this.data()?.totalPages || 0)) {
      this.currentPage.set(page);
      this.loadMessages(estId);
    }
  }

  previousPage() {
    if (this.currentPage() > 0) {
      this.goToPage(this.currentPage() - 1);
    }
  }

  nextPage() {
    const data = this.data();
    if (data && this.currentPage() < data.totalPages - 1) {
      this.goToPage(this.currentPage() + 1);
    }
  }



  private validateMessage(titre: string, contenu: string): string[] {
    const errors: string[] = [];

    if (!titre.trim()) errors.push(this.translate.instant('actualite.journal.validation.titleRequired'));
    if (!contenu.trim()) errors.push(this.translate.instant('actualite.journal.validation.contentRequired'));
    if (contenu.length > 300) errors.push(this.translate.instant('actualite.journal.validation.contentTooLong'));

    return errors;
  }

  updateNouveauCommentaire(messageId: string, value: string) {
    this.nouveauCommentaire.update(state => ({ ...state, [messageId]: value }));
  }



  private filterUsersForMention(query: string) {
    return this.users()
      .filter((u: any) => {
        if (!u.email || typeof u.email !== 'string' || !u.email.includes('@')) {
          return false;
        }
        const mentionFormat = u.mentionFormat || u.email.split('@')[0];
        return mentionFormat.toLowerCase().includes(query) || u.name.toLowerCase().includes(query);
      })
      .slice(0, 10);
  }

  onTextareaInput(event: any) {
    const text = event.target.value;
    const cursorPos = event.target.selectionStart;
    const beforeCursor = text.substring(0, cursorPos);
    const match = beforeCursor.match(/@([\w\.-]*)$/);

    if (match) {
      const query = match[1].toLowerCase();
      this.filteredUsers = this.filterUsersForMention(query);
      this.showMentionDropdown = true;
    } else {
      this.showMentionDropdown = false;
    }

    this.nouveauMessage = text;
  }

  selectMention(user: any, textarea: HTMLTextAreaElement) {
    const text = textarea.value;
    const cursorPos = textarea.selectionStart;
    const beforeCursor = text.substring(0, cursorPos);
    const afterCursor = text.substring(cursorPos);

    if (!user.email || typeof user.email !== 'string' || !user.email.includes('@')) {
      return;
    }
    const mentionFormat = user.mentionFormat || user.email.split('@')[0];
    const newText = beforeCursor.replace(/@[\w\.-]*$/, `@${mentionFormat} `) + afterCursor;
    this.nouveauMessage = newText;
    this.showMentionDropdown = false;

    setTimeout(() => textarea.focus());
  }

  onCommentInput(event: any, messageId: string) {
    const text = event.target.value;
    const cursorPos = event.target.selectionStart;
    const beforeCursor = text.substring(0, cursorPos);
    const match = beforeCursor.match(/@([\w\.-]*)$/);

    if (match) {
      const query = match[1].toLowerCase();
      this.filteredUsers = this.filterUsersForMention(query);
      this.showCommentMentionDropdown.update(state => ({ ...state, [messageId]: true }));
    } else {
      this.showCommentMentionDropdown.update(state => ({ ...state, [messageId]: false }));
    }

    this.updateNouveauCommentaire(messageId, text);
  }

  selectCommentMention(user: any, textarea: HTMLTextAreaElement, messageId: string) {
    const text = textarea.value;
    const cursorPos = textarea.selectionStart;
    const beforeCursor = text.substring(0, cursorPos);
    const afterCursor = text.substring(cursorPos);

    if (!user.email || typeof user.email !== 'string' || !user.email.includes('@')) {
      return;
    }
    const mentionFormat = user.mentionFormat || user.email.split('@')[0];
    const newText = beforeCursor.replace(/@[\w\.-]*$/, `@${mentionFormat} `) + afterCursor;
    this.updateNouveauCommentaire(messageId, newText);
    this.showCommentMentionDropdown.update(state => ({ ...state, [messageId]: false }));

    setTimeout(() => textarea.focus());
  }



  splitMentions(text: string): { text: string; isMention: boolean }[] {
    const parts: { text: string; isMention: boolean }[] = [];
    const mentionRegex = /@([a-zà-ÿ-]+(?:\.[a-zà-ÿ-]+)?)/gi;
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: text.substring(lastIndex, match.index), isMention: false });
      }
      parts.push({ text: match[0], isMention: true });
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push({ text: text.substring(lastIndex), isMention: false });
    }

    return parts;
  }





  supprimerMessage(messageId: string) {
    this.confirmModalTitle.set(this.translate.instant('actualite.journal.confirm.deleteMessageTitle'));
    this.confirmModalMessage.set(this.translate.instant('actualite.journal.confirm.deleteMessageMessage'));
    this.pendingAction = () => this.confirmDeleteMessage(messageId);
    this.showConfirmModal.set(true);
  }

  private confirmDeleteMessage(messageId: string) {
    const estId = this.session.selectedId();
    if (!estId) return;

    this.api.deleteMessage(estId, messageId).subscribe({
      next: () => {
        this.loadMessages(estId);
        this.toastService.show(this.translate.instant('actualite.journal.toast.deletedTitle'), 'success', 3000, this.translate.instant('actualite.journal.toast.messageDeleted'));
      },
      error: (err) => {
        console.error('Erreur suppression message - Status:', this.sanitizeForLog(err?.status || 'unknown'));
        this.toastService.show(this.translate.instant('actualite.journal.toast.errorTitle'), 'error', 4000, this.translate.instant('actualite.journal.toast.deleteError'));
      }
    });
  }

  supprimerCommentaire(commentaireId: string) {
    this.confirmModalTitle.set(this.translate.instant('actualite.journal.confirm.deleteCommentTitle'));
    this.confirmModalMessage.set(this.translate.instant('actualite.journal.confirm.deleteCommentMessage'));
    this.pendingAction = () => this.confirmDeleteCommentaire(commentaireId);
    this.showConfirmModal.set(true);
  }

  private confirmDeleteCommentaire(commentaireId: string) {
    const estId = this.session.selectedId();
    if (!estId) return;

    this.api.deleteCommentaire(estId, commentaireId).subscribe({
      next: () => {
        this.loadMessages(estId);
        this.toastService.show(this.translate.instant('actualite.journal.toast.deletedTitle'), 'success', 3000, this.translate.instant('actualite.journal.toast.commentDeleted'));
      },
      error: (err) => {
        console.error('Erreur suppression commentaire - Status:', this.sanitizeForLog(err?.status || 'unknown'));
        this.toastService.show(this.translate.instant('actualite.journal.toast.errorTitle'), 'error', 4000, this.translate.instant('actualite.journal.toast.deleteError'));
      }
    });
  }

  onConfirmAction() {
    if (this.pendingAction) {
      this.pendingAction();
      this.pendingAction = null;
    }
    this.showConfirmModal.set(false);
  }

  onCancelAction() {
    this.pendingAction = null;
    this.showConfirmModal.set(false);
  }

  startEditMessage(message: Message) {
    this.editingMessageId.set(message.id);
    this.editTitre = message.titre;
    this.editContenu = message.contenu;
  }

  cancelEditMessage() {
    this.editingMessageId.set(null);
    this.editTitre = '';
    this.editContenu = '';
  }

  saveEditMessage(messageId: string) {
    const estId = this.session.selectedId();
    if (!estId) return;

    this.api.updateMessage(estId, messageId, {
      titre: this.editTitre,
      contenu: this.editContenu
    }).subscribe({
      next: () => {
        this.editingMessageId.set(null);
        this.loadMessages(estId);
        this.toastService.show(this.translate.instant('actualite.journal.toast.editedTitle'), 'success', 3000, this.translate.instant('actualite.journal.toast.messageEdited'));
      },
      error: (err) => {
        console.error('Erreur modification message - Status:', this.sanitizeForLog(err?.status || 'unknown'));
        this.toastService.show(this.translate.instant('actualite.journal.toast.errorTitle'), 'error', 4000, this.translate.instant('actualite.journal.toast.editError'));
      }
    });
  }

  startEditCommentaire(commentaire: Commentaire) {
    this.editingCommentaireId.set(commentaire.id);
    this.editContenu = commentaire.contenu;
  }

  cancelEditCommentaire() {
    this.editingCommentaireId.set(null);
    this.editContenu = '';
  }

  saveEditCommentaire(commentaireId: string) {
    const estId = this.session.selectedId();
    if (!estId) return;

    this.api.updateCommentaire(estId, commentaireId, {
      contenu: this.editContenu
    }).subscribe({
      next: () => {
        this.editingCommentaireId.set(null);
        this.loadMessages(estId);
        this.toastService.show(this.translate.instant('actualite.journal.toast.editedTitle'), 'success', 3000, this.translate.instant('actualite.journal.toast.commentEdited'));
      },
      error: (err) => {
        console.error('Erreur modification commentaire - Status:', this.sanitizeForLog(err?.status || 'unknown'));
        this.toastService.show(this.translate.instant('actualite.journal.toast.errorTitle'), 'error', 4000, this.translate.instant('actualite.journal.toast.editError'));
      }
    });
  }

  private static readonly MENTION_REGEX = /@([a-zà-ÿ-]+(?:\.[a-zà-ÿ-]+)?)/gi;

  private sanitizeForLog(input: any): string {
    if (input == null) return 'null';
    const str = String(input);
    return str.replace(/[\r\n\t]/g, ' ').substring(0, 200);
  }

  private extractMentions(content: string): { mentionedUserIds: string[], mentionAll: boolean } {
    const mentionedUserIds: string[] = [];
    let mentionAll = false;

    // Regex pour capturer @prénom.nom (avec tirets) ou @tous
    const mentionRegex = new RegExp(ActualiteComponent.MENTION_REGEX.source, ActualiteComponent.MENTION_REGEX.flags);
    let match;

    while ((match = mentionRegex.exec(content)) !== null) {
      const mention = match[1].toLowerCase().trim();

      if (mention === 'tous' || mention === 'all' || mention === 'everyone') {
        mentionAll = true;
      } else {
        const userId = this.userMap.get(mention);
        if (userId && !mentionedUserIds.includes(userId)) {
          mentionedUserIds.push(userId);
        }
      }
    }

    return { mentionedUserIds, mentionAll };
  }

  addEmoji(event: any) {
    if (!event?.emoji?.native) {
      console.error('Invalid emoji event data');
      return;
    }

    this.nouveauMessage += event.emoji.native;
    this.showEmojiPicker = false;
  }

  addCommentEmoji(event: any, messageId: string) {
    if (!event?.emoji?.native) {
      console.error('Invalid emoji event data');
      return;
    }

    const current = this.nouveauCommentaire()[messageId] || '';
    this.updateNouveauCommentaire(messageId, current + event.emoji.native);
    this.showCommentEmojiPicker.update(state => ({ ...state, [messageId]: false }));
  }

  toggleCommentEmojiPicker(messageId: string) {
    this.showCommentEmojiPicker.update(state => ({ ...state, [messageId]: !state[messageId] }));
  }

  toggleComposer() {
    this.showComposer.update(v => !v);
  }



  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target || typeof target.closest !== 'function') return;

    try {
      if (!target.closest('.emoji-picker') && !target.closest('.btn-emoji') && !target.closest('.btn-emoji-comment')) {
        this.showEmojiPicker = false;
        this.showCommentEmojiPicker.set({});
      }
    } catch (err) {
      console.error('Error in document click handler - Status:', this.sanitizeForLog(err instanceof Error ? err.message : 'unknown'));
    }
  }

  onTemplateSelect() {
    if (this.selectedTemplate) {
      const template = this.newsTemplates().find(t => t.id === this.selectedTemplate);
      if (template) {
        if (!this.nouveauTitre.trim()) {
          this.nouveauTitre = template.name;
        }
        this.nouveauMessage = template.content;
      }
    } else {
      this.nouveauTitre = '';
      this.nouveauMessage = '';
    }
  }
}
